export const PUBLIC_TIER_LABELS = {
  basic: "Basic Hosting",
  essential: "Essential",
  premium: "Premium",
} as const;

export type PublicTier = keyof typeof PUBLIC_TIER_LABELS;

export const REVIEWED_TIER = "dev" as const;
export type ManagedTier = PublicTier | typeof REVIEWED_TIER;

export const BASIC_PUBLIC_PRICE_ID_PLACEHOLDER = "price_1THNBK53YIqi52EphPP00loC" as const;

// Basic Hosting still uses the legacy public tier wiring in some customer-facing
// flows. Keep reviewed pricing semantics separate from this.
export const PUBLIC_TIER_PRICE_IDS = {
  basic: BASIC_PUBLIC_PRICE_ID_PLACEHOLDER,
  essential: "price_1ShmoC53YIqi52EplfiG8VB2",
  premium: "price_1SbFT353YIqi52Epb9WL8laA",
} as const;

export const PUBLIC_TIER_MONTHLY_PRICES_AUD = {
  basic: 12.99,
  essential: 50,
  premium: 185,
} as const;

export const PUBLIC_TIER_CANCELLATION_FEES_AUD = {
  basic: 12.99,
  essential: 50,
  premium: 99,
} as const;

export const PUBLIC_STANDARD_PRICE_TIERS = {
  basic: [
    { min: 1, price_id: PUBLIC_TIER_PRICE_IDS.basic },
  ],
  essential: [
    { min: 1, price_id: PUBLIC_TIER_PRICE_IDS.essential },
  ],
  premium: [
    { min: 5000, price_id: "price_1Shmpn53YIqi52EpN1nyoIab" },
    { min: 2000, price_id: "price_1Shmqc53YIqi52EpuIfREZGs" },
    { min: 1000, price_id: "price_1ShmpV53YIqi52EpkBu3vC36" },
    { min: 1, price_id: PUBLIC_TIER_PRICE_IDS.premium },
  ],
} as const;

export const STANDARD_PRICE_ID_TO_TIER = Object.entries(PUBLIC_STANDARD_PRICE_TIERS).reduce<
  Record<string, PublicTier>
>((acc, [tier, tiers]) => {
  for (const entry of tiers) {
    acc[entry.price_id] = tier as PublicTier;
  }
  return acc;
}, {});

export const PUBLIC_TRIAL_TIERS = ["essential"] as const;

export const isPublicTier = (value: string | null | undefined): value is PublicTier =>
  value === "basic" || value === "essential" || value === "premium";

export const isManagedTier = (value: string | null | undefined): value is ManagedTier =>
  isPublicTier(value) || value === REVIEWED_TIER;

export const isConfiguredPublicTierPriceId = (
  tier: PublicTier,
  priceId: string | null | undefined = PUBLIC_TIER_PRICE_IDS[tier],
) => {
  if (!priceId) return false;
  if (tier === "basic" && priceId === BASIC_PUBLIC_PRICE_ID_PLACEHOLDER) return false;
  return true;
};

export const ensureConfiguredPublicTierPriceId = (
  tier: PublicTier,
  priceId: string | null | undefined = PUBLIC_TIER_PRICE_IDS[tier],
) : string => {
  const resolvedPriceId = priceId ?? "";
  if (!isConfiguredPublicTierPriceId(tier, priceId)) {
    const error = new Error(
      `${PUBLIC_TIER_LABELS[tier]} public Stripe pricing is not configured. Replace the placeholder Stripe price ID before enabling checkout.`,
    ) as Error & { statusCode?: number };
    error.statusCode = 503;
    throw error;
  }

  return resolvedPriceId;
};

export const resolvePublicPriceIdForCount = (tier: PublicTier, count: number) => {
  const tiers = PUBLIC_STANDARD_PRICE_TIERS[tier] || [];
  const normalized = Math.max(0, count);
  for (const entry of tiers) {
    if (normalized >= entry.min) return ensureConfiguredPublicTierPriceId(tier, entry.price_id);
  }
  return ensureConfiguredPublicTierPriceId(tier);
};

export const getPublicTierLabel = (tier: PublicTier) => PUBLIC_TIER_LABELS[tier];
